import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useModules, usePrograms } from "../../hooks/useRedux";
import {
  setModulesUploadProgress,
  clearModulesUploadProgress,
  updateModuleAction,
} from "../../store";
import ModuleForm from "./ModuleForm";
import { Module } from "../../store/slices/types";
import { endPoints } from "../../services/endpoints";
import { API_BASE_URL } from "../../config/api";
import axios from "axios";

const EditModule: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { modules, loading, uploadProgress } = useModules();
  const { programs } = usePrograms();
  const [saving, setSaving] = useState(false);

  const module = modules.find((m) => (m._id || m.id) === id) as
    | Module
    | undefined;

  const handleSave = async (moduleData: Partial<Module>, videoFile?: File) => {
    if (!id) {
      toast.error("Invalid module ID");
      return;
    }

    try {
      setSaving(true);
      dispatch(clearModulesUploadProgress());

      // Create FormData with module data and video file
      const formData = new FormData();

      // Append all module fields
      if (moduleData.title) formData.append("title", moduleData.title);
      if (moduleData.difficulty)
        formData.append("difficulty", moduleData.difficulty);
      if (moduleData.content) formData.append("content", moduleData.content);
      if (moduleData.programId)
        formData.append("programId", moduleData.programId);
      if (moduleData.weekNumber !== undefined)
        formData.append("weekNumber", String(moduleData.weekNumber));
      if (moduleData.order !== undefined)
        formData.append("order", String(moduleData.order));
      if (moduleData.duration !== undefined)
        formData.append("duration", String(moduleData.duration));
      if (moduleData.type) formData.append("type", moduleData.type);
      if (moduleData.isActive !== undefined)
        formData.append("isActive", String(moduleData.isActive));
      formData.append("updatedAt", String(Date.now()));

      // Append tags as JSON string
      if (moduleData.tags && Array.isArray(moduleData.tags)) {
        formData.append("tags", JSON.stringify(moduleData.tags));
      }

      // Append video file if provided, otherwise keep existing videoUrl
      if (videoFile) {
        formData.append("video", videoFile);
      } else if (moduleData.videoUrl) {
        formData.append("videoUrl", moduleData.videoUrl);
      }

      const token = localStorage.getItem("authToken");

      // Single API call with progress tracking
      const response = await axios.put(
        `${API_BASE_URL}${endPoints.modules.update(id)}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token ? `Bearer ${token}` : "",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              dispatch(setModulesUploadProgress(percentCompleted));
            }
          },
        }
      );

      dispatch(clearModulesUploadProgress());

      if (response.data?.success || response.data) {
        const updatedModule = {
          ...(response.data?.data || response.data),
          _id: id,
        };
        dispatch(updateModuleAction({ id, data: updatedModule }));
        toast.success("module is updated");
        navigate("/modules");
      } else {
        toast.error("Failed to update module");
      }
    } catch (error: any) {
      console.error("Failed to update module:", error);
      dispatch(clearModulesUploadProgress());
      toast.error(
        `❌ Failed to update module: ${
          error?.response?.data?.message || error?.message || "Unknown error"
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/modules");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!module) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          Module not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          mb: 4,
        }}
      >
        <Typography variant='h4' fontWeight='700' gutterBottom>
          Edit Module
        </Typography>
        <Typography variant='body1' sx={{ opacity: 0.9 }}>
          Update your learning module
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <ModuleForm
          module={module}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          uploadProgress={uploadProgress}
          programs={programs}
        />
      </Paper>
    </Box>
  );
};

export default EditModule;
